import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import dayjs, { Dayjs } from "dayjs";
import styled from "styled-components";
import { GetUserBusinessUnits } from "app/features/userSlice";
import { getBusinesses } from "app/features/businessesSlice";
import { LocalStorageHelper } from "lib/helpers/localStorageHelper";
import Select from "ui/molecules/Select";
import DatePicker from "ui/molecules/DatePicker";
import SaveIcon from "@mui/icons-material/Save";
import LoadingButton from "@mui/lab/LoadingButton";
import ErrorMessage from "ui/atoms/ErrorMessage";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";

interface State {
  businessUnitCode: string;
  status: string;
  finalDate: string;
}

const initialState: State = {
  businessUnitCode: "ALL",
  status: "activo",
  finalDate: dayjs()?.format("YYYY-MM-DD"),
};

const Filters = ({ isLoading, handleSelect, handleExportFile, isButtonEnabled, loadingFile }: any) => {
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useAppDispatch();
  const [state, setState] = useState(initialState);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const { userBusinesses } = useAppSelector((state) => state.user) ?? [];
  const userLogged = LocalStorageHelper.getItem("user-logged");

  const validateEmptyState = () => {
    const stateKeys = Object.keys(state as any);
    const emptyState = stateKeys.filter((key) => state[key as keyof State] === "");
    if (emptyState.length > 0) {
      return `Todos los campos son obligatorios`;
    }

    if (!dayjs(state?.finalDate).isValid() || state?.finalDate === undefined) {
      return `La fecha no es válida`;
    }
    return "";
  };

  useEffect(() => {
    dispatch(getBusinesses());
    if (isAuthenticated) {
      const userLogged = accounts[0]?.username;
      dispatch(GetUserBusinessUnits(userLogged));
    }
  }, [userLogged]);

  const handleBusinessCodeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setState((prevState) => ({ ...prevState, businessUnitCode: event.target.value as string }));
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value;
    setState((prevState) => ({ ...prevState, status: value }));
  };

  const handleDateChange = (date: Dayjs) => {
    setState((prevState) => ({ ...prevState, finalDate: date?.format("YYYY-MM-DD") }));
  };

  function handleSubmit() {
    setIsDisabled(false);
    handleSelect(state);
  }

  const getDatePickerLabel = () => {
    if (state.status === "inactivo") {
      return "Inactivos vinculados hasta";
    }
    return "Activos vinculados hasta";
  };

  return (
    <div>
      <TopContainer>
        <FormControl fullWidth>
          <Select
            id='business'
            mapKey='code'
            name='value1'
            margin='normal'
            mapValue='fullName'
            label='Seleccione Finca'
            value={state.businessUnitCode}
            onChange={handleBusinessCodeChange}
            dataSource={[{ code: "ALL", fullName: "TODOS" }, ...userBusinesses]}
          />
        </FormControl>
        <FormControl fullWidth sx={{ marginLeft: { xl: 2, sm: 2, xs: 0 } }}>
          <DatePickerStyled
            label={getDatePickerLabel()}
            name='finalDate'
            value={state.finalDate}
            onChange={handleDateChange}
            disabled={state?.status === "activo"}
          />
        </FormControl>
      </TopContainer>
      <FormControl component='fieldset'>
        <RadioGroup row aria-label='status' name='status' value={state.status} onChange={handleStatusChange}>
          <FormControlLabel value='activo' control={<Radio />} label='Activo' />
          <FormControlLabel value='inactivo' control={<Radio />} label='Inactivo' />
          <FormControlLabel value='all' control={<Radio />} label='Todos' />
          <ButtonStyled
            color='primary'
            variant='outlined'
            loading={loadingFile}
            loadingPosition='start'
            startIcon={<CloudDownloadIcon />}
            onClick={handleExportFile}
            disabled={isButtonEnabled || loadingFile}
          >
            {`${loadingFile ? "Descargando..." : "Descargar Excel"}`}
          </ButtonStyled>
        </RadioGroup>
      </FormControl>
      <ErrorMessage margin='0 0 7px'>{validateEmptyState()}</ErrorMessage>
      <ButtonContainer>
        <LoadingButton
          fullWidth
          loading={isLoading}
          color='primary'
          variant='contained'
          loadingPosition='start'
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={isDisabled || validateEmptyState() !== ""}
        >
          <span>Buscar</span>
        </LoadingButton>
      </ButtonContainer>
    </div>
  );
};

export default Filters;

const DatePickerStyled = styled(DatePicker)`
  width: 100%;
`;

const TopContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  button {
    margin-top: 0;
    margin-bottom: 1rem;
  }
`;

const ButtonStyled = styled(LoadingButton)`
  min-width: 200px;
  height: 35px;
  margin: 5px 0.25rem !important;
`;
